import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { P, Small } from '@/components/ui/typography';
import { View, ActivityIndicator, Text, Pressable } from 'react-native';
import { formatTime, formatDate, numberToTimestamp } from 'shared-utils';
import { parse, isValid } from 'date-fns';
import { trpc } from '@/trpc/react';
import Toast from 'react-native-toast-message';
import { useCrew } from '@/context/CrewProvider';
import { Button } from '@/components/ui';
import { Notification } from '../../types/Notification';

export const mapJobDateChangeType = (type: string) => {
  switch (type) {
    case "venue": return "Venue";
    case "started_at": return "Date";
    case "started_time": return "Time";
    case "hours": return "Hours";
    case "crew": return "Crew";
    case "cancel": return "Cancelled";
  }
}

export const mapJobDateChangeValue = (type: string, value: string) => {
  switch (type) {
    case "venue": return value;
    case "started_at": {
      try {
        // Parse MM/DD/YYYY format
        const parsedDate = parse(value, 'M/d/yyyy', new Date());
        if (isValid(parsedDate)) {
          return formatDate(parsedDate, "dd/MM/yyyy");
        }
      } catch (e) {
      }
      return formatDate(value, "dd/MM/yyyy");
    }
    case "started_time": return numberToTimestamp(parseInt(value));
    case "hours": return `${value} hours`;
    case "crew": return value;
    case "cancel": return value === "true" ? "Yes" : "No";
    default: return value;
  }
}

interface JobDateChangeProps {
  notification: Notification
}

type JobChangeData = {
  changes: {
    value_before: string;
    value_after: string;
    type: string;
  }[];
  venue_name: string;
  venue_address_line_1: string;
  date_started_at: string;
  date_started_time: number;
  date_ended_time: number;
  job_date_id: number;
  client_name: string;
}

const PENDING_JOB_STATE = 1;

const mapNotificationToJob = (notification: Notification) => {
  const data = notification.data as JobChangeData;
  return {
    subject: notification.subject,
    data: {
      changes: data.changes,
      venue_name: data.venue_name,
      venue_address_line_1: data.venue_address_line_1,
      date_started_at: data.date_started_at,
      date_started_time: data.date_started_time,
      date_ended_time: data.date_ended_time,
      job_date_id: data.job_date_id,
      client_name: data.client_name,
    },
    subject_type_id: notification.subject_type_id,
  }
}

const JobDateChange: React.FC<JobDateChangeProps> = ({ notification }) => {
  const { currentCrewMember } = useCrew();

  const mappedNotification = mapNotificationToJob(notification);

  const { data: crewList, refetch, isLoading } = trpc.crew.getCrewListForJobDate.useQuery(mappedNotification.subject_type_id);
  const { mutate: updateStatus, isPending: isUpdatingStatus } = trpc.jobDateListRouter.updateJobCrewDateStatus.useMutation({
    onSuccess(_response, variables) {
      refetch();
      Toast.show({
        type: 'success',
        text1: `Successfully ${variables.status === 2 ? 'accepted' : 'declined'} job.`
      });
    },
    onError(error) {
      refetch();
      Toast.show({
        type: 'error',
        text1: 'Something went wrong',
        text2: error.message || 'Please try again.'
      });
    },
  });

  const { mutate: markAsRead } = trpc.notification.markAsRead.useMutation();

  const onAccept = (jobDateCrewId: number) => {
    updateStatus({ params: jobDateCrewId, status: 2 });
    markAsRead(notification.id);
  }

  const onReject = (jobDateCrewId: number) => {
    updateStatus({ params: jobDateCrewId, status: 0 });
    markAsRead(notification.id);
  }

  const authedJobCrew = crewList?.find((crew) => crew.crew_id === currentCrewMember?.id && crew.job_date_id === mappedNotification.data.job_date_id);

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardContent>
          <View className="flex items-center justify-center py-8">
            <ActivityIndicator size="large" />
          </View>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <Card className="border border-red-500">
        <CardHeader>
          <CardTitle><Small>There have been changes to this job</Small></CardTitle>
        </CardHeader>
      </Card>
      <CardHeader>
        <CardTitle>{mappedNotification.subject}</CardTitle>
        <CardDescription>
          <P className="font-bold">{mappedNotification.data.client_name}</P>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CardDescription className="font-bold">
          <P>{mappedNotification.data.venue_name}</P>
        </CardDescription>
        <CardDescription>
          <P>{mappedNotification.data.venue_address_line_1}</P>
        </CardDescription>
        <CardDescription className="mt-2">
          <View className="flex flex-row">
            <P className="w-1/2">{mappedNotification.data.date_started_at}{' '}</P>
            <P className="w-1/2">{mappedNotification.data.date_started_time} - {mappedNotification.data.date_ended_time}</P>
          </View>
        </CardDescription>
        <View className="mt-4 mb-2">
          <P className="font-bold mb-2">The following changes have been made:</P>
          {mappedNotification.data.changes.map((change, index) => (
            <View key={index} className="mb-3 bg-gray-50 p-3 rounded-md">
              <P className="font-semibold text-gray-800">{mapJobDateChangeType(change.type)}</P>
              <View className="flex-row mt-1">
                <View className="flex-1 mr-2">
                  <P className="text-xs text-gray-500">Previous</P>
                  <P className="text-red-500">{mapJobDateChangeValue(change.type, change.value_before)}</P>
                </View>
                <View className="flex-1">
                  <P className="text-xs text-gray-500">New</P>
                  <P className="text-green-500">{mapJobDateChangeValue(change.type, change.value_after)}</P>
                </View>
              </View>
            </View>
          ))}
        </View>
        {authedJobCrew?.state === PENDING_JOB_STATE ? (
          <View className="flex flex-row mb-2 mt-4">
            <Button className=' mr-2 w-1/2 bg-success' disabled={isUpdatingStatus} onPress={() => onAccept(authedJobCrew.id)}>
              <P className='text-white'>Accept</P>
            </Button>
            {!mappedNotification.data.changes.some((x) => x.type === "cancel") ? (
              <Button className='w-1/2' variant="destructive" disabled={isUpdatingStatus} onPress={() => onReject(authedJobCrew.id)}>
                <P className='text-white'>Decline</P>
              </Button>
            ) : null}
          </View>
        ) : (
          <View>
            <View className="flex-row justify-end mt-4">
              <Pressable
                className="bg-gray-100 px-3 py-2 rounded-md"
                onPress={() => {
                  markAsRead(notification.id);
                  Toast.show({
                    type: 'success',
                    text1: 'Notification marked as read'
                  });
                }}
              >
                <Text className="text-gray-600 text-sm">Mark as read</Text>
              </Pressable>
            </View>
          </View>
        )}
      </CardContent>
    </Card>
  );
};

export default JobDateChange;